import React, { useCallback } from 'react';
import type { AssetTypeEnum } from 'venn-api';
import styled from 'styled-components';
import SimpleFilter from './SimpleFilter';
import { FundUtils, useHasFF } from 'venn-utils';
import compact from 'lodash/compact';
import { GetColor, Icon } from 'venn-ui-kit';
import { Tooltip, TooltipContentWithArrow, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const fieldName = 'Asset Type';

interface AssetTypeFilterProps {
  className?: string;
  onFilter: (updated: AssetTypeEnum[]) => void;
  selected: AssetTypeEnum[];
  closeOnOnlyClick?: boolean;
  isAdvanced?: boolean;
  menuWidth?: number;
}

const AssetTypeFilter = ({
  className,
  selected,
  onFilter,
  closeOnOnlyClick,
  isAdvanced,
  menuWidth,
}: AssetTypeFilterProps) => {
  const hasCanadaRestriction = useHasFF('canada_restriction');
  const hasVennDailyPrivateAssetReturnsFF = useHasFF('venn_daily_private_asset_returns_ff');

  const FILTERED_TYPES: AssetTypeEnum[] = compact([
    'ETF',
    hasCanadaRestriction ? undefined : 'STOCK',
    'MUTUAL_FUND',
    'INDEX',
    'CURRENCY',
    'CRYPTO_REFERENCE_RATE',
    'CATEGORY_GROUPING',
    hasVennDailyPrivateAssetReturnsFF ? 'VENN_DAILY_PRIVATE_ASSET_RETURNS' : undefined,
  ]);

  const ASSET_TYPE_OPTIONS = FILTERED_TYPES.map((assetType) => {
    const config = FundUtils.getAssetTypeConfig(assetType);

    return {
      label: config.name,
      value: assetType,
      badge: config.tooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <InfoIcon type="info-circle" prefix="far" className="ml-[2px]" />
            </span>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContentWithArrow side="top" className="max-w-[280px]">
              {config.tooltip}
            </TooltipContentWithArrow>
          </TooltipPortal>
        </Tooltip>
      ) : undefined,
    };
  });

  const handleFilter = useCallback(
    (updated: string | string[]) => {
      const updatedAssetTypeEnum = updated as AssetTypeEnum[];
      onFilter(Array.from(updatedAssetTypeEnum));
    },
    [onFilter],
  );

  return (
    <SimpleFilter
      className={cn('inline-flex', className)}
      label={fieldName}
      items={ASSET_TYPE_OPTIONS}
      initialSelected={selected}
      menuWidth={menuWidth ?? 320}
      onFilter={handleFilter}
      closeOnOnlyClick={closeOnOnlyClick}
      isAdvanced={isAdvanced}
    />
  );
};

export default AssetTypeFilter;

const InfoIcon = styled(Icon)`
  color: ${GetColor.GreyScale.Grey90};
  line-height: normal;
  font-size: 13px;
`;
